import React, { useEffect, useMemo } from 'react';
import {Logo } from "@/assets";
import LayoutContainer from "@/components/LayoutPageMobile/LayoutContainer.tsx";
import {Row, Text} from "@keyslabor/shared/ui";
import {useInterviewOfferPreview, useInterviewOfferConfirmed} from "@/api/services.ts";
import {useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import Button from "@keyslabor/shared/ui/Button";
import { SuccessIcon } from '@/assets/offer'
import OfferConfirmedScreen from "./OfferConfirmedScreen.tsx";
import { languageToLocale } from '@/hooks/useAppLanguage.ts';
import OfferDetails from "@/pages/OfferConfirmation/OfferDetails.tsx";
import InpersonInterviewDetails from "@/pages/OfferConfirmation/InpersonInterviewDetails.tsx";
import { InterviewOfferPreviewOutput, Language } from '@/api/types.ts';

const getIsInPersonInterviewOffer = (data: InterviewOfferPreviewOutput) => {
    return !!data.offer.in_person_interview_date
}

const useOfferLanguage = (data: InterviewOfferPreviewOutput | undefined): Language => {
    return useMemo(() => {
        return data?.offer?.language ?? 'english';
    }, [data]);
}

const OfferConfirmation = () => {
    const { t, i18n } = useTranslation('confirmation')
    const [offerConfirmed, setOfferConfirmed] = React.useState(false);
    const [searchParams] = useSearchParams()
    const invitationKey = searchParams.get('invitation_key')
    const { data, error, isLoading } = useInterviewOfferPreview(invitationKey);
    const offerLanguage = useOfferLanguage(data);
    const { mutateAsync: triggerInterviewOfferConfirmed } = useInterviewOfferConfirmed();
    const handleOrderConfirmed = async () => {
        await triggerInterviewOfferConfirmed({invitation_key: invitationKey});
        setOfferConfirmed(true);
    }

    useEffect(() => {
        i18n.changeLanguage(languageToLocale[offerLanguage] ?? offerLanguage);
    }, [offerLanguage]);

    const isOfferConfirmed = !data?.show_cta || offerConfirmed;

    if (error || !data) {
        return <OfferConfirmedScreen loading={isLoading} />
    }

    const isInPersonInterviewOffer = getIsInPersonInterviewOffer(data);

    return (
        <LayoutContainer
            loading={isLoading}
            footer={isOfferConfirmed ? (
                <Row
                    py="20px"
                    borderTopLeftRadius="4px"
                    borderTopRightRadius="4px"
                    width="100%"
                    flexCenter
                >
                    <Text fontWeight="700" textAlign="center" mb="30px" px="12px" fontSize="20px">
                        {t('offerConfirmedMessage')}
                    </Text>
                    <SuccessIcon/>
                </Row>
            ) : (
                <Row
                    py="20px"
                    borderTopLeftRadius="4px"
                    borderTopRightRadius="4px"
                    width="100%"
                    flexCenter
                >
                    <Text fontWeight="600" textAlign="center" mb="30px" px="12px" fontSize="20px" color={'#E01212'}>
                        {t(`${isInPersonInterviewOffer ? 'interview': 'offer'}.message`)}
                    </Text>
                    <Button
                        overflow="hidden"
                        position="relative"
                        maxWidth="332px"
                        width="100%"
                        kind="large"
                        onClick={handleOrderConfirmed}
                    >
                        {t('confirmLabel')}
                    </Button>
                </Row>
            )}
        >
            <Row flexCenter maxWidth="480px" justifySelf="center">
                <Logo/>
                <Row flexCenter mt="70px">
                    <Text fontWeight="900" mt="" fontSize="20px" upper textAlign="center">
                        {t('hello')} <br/>
                        {data?.offer?.name}
                    </Text>
                </Row>
                {isInPersonInterviewOffer ? <InpersonInterviewDetails data={data.offer} /> : <OfferDetails data={data.offer} /> }
            </Row>
        </LayoutContainer>
    );
};

export default OfferConfirmation;
